// ============================================================
// MEET IMPORT PANEL — 「Google Meet から取り込む」共有コンポーネント（2026-07-10）。
// NotesList.tsx 内の MeetImportSection をそのまま抽出したもの（ロジック・見た目は移植元に忠実）。
//   ・カレンダーの過去14日の会議を**タイトルのみ**自動表示（本文は取らない）。
//   ・「議事録を取得する」を押した会議だけ添付の議事録Doc（Docs API）
//     → 既存 summarizeTranscript callable で議事録＋タスク生成。
//   ・状態は meetingImports コレクション（doc id = calendar eventId）で全員共有：
//     誰か1人が取得（imported）すれば全員の一覧から消える／「取得しない」（dismissed）も
//     会議単位で全員共有・折りたたみから復元（status='pending' に差し替え）できる。
//
// variant:
//   'full'    … /notes 用。従来の MeetImportSection と同一表示
//               （未連携は1行案内・未処理最大10件・非表示/取り込み済み折りたたみ付き）。
//   'compact' … ホーム用。未処理で議事録Docありの会議のみ最大3件＋「すべて見る→/notes」。
//               未連携・対象0件のときはパネルごと非表示（ホームを汚さない）。
// ============================================================
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth, useViewer } from '../auth/AuthContext';
import { DATA_BACKEND, getAdapter, type MeetingImportWrite } from '../data/adapter';
import { functions } from '../lib/firebase';
import { isAdmin, isExec, visibleProjects } from '../lib/permissions';
import { PROJECTS } from '../data/demoData';
import { Icon } from './Icons';
import {
  fetchGoogleDocText,
  fetchRecentMeetings,
  GoogleApiError,
  type RecentMeeting,
} from '../lib/google';
import type { MeetingImport } from '../types/models';

/** summarizeTranscript callable の入出力（functions/src/index.ts の入力検証と一致）。 */
interface SummarizePayload {
  transcriptText: string;
  projectHint?: string;
  restricted?: boolean;
  /** カレンダーの実イベント名（note.title に優先採用される）。 */
  titleHint?: string;
  /** 会議の開催日 'YYYY-MM-DD'（note.date に採用される）。 */
  dateHint?: string;
  /** 会議の出席者メール（participantIds 解決の一次ソース・最大50件）。 */
  attendeeEmails?: string[];
}
interface SummarizeResult {
  noteId: string;
  taskCount: number;
  title: string;
}

/** compact でホームに出す最大件数。 */
const COMPACT_LIMIT = 3;

export default function MeetImportPanel({ variant }: { variant: 'full' | 'compact' }) {
  const viewer = useViewer();
  const { googleAccessToken, signInWithGoogle } = useAuth();
  const execView = isAdmin(viewer) || isExec(viewer);
  // AI議事録化はライブ（firebase）接続時のみ（Upload.tsx と同じゲート）。
  const aiEnabled = DATA_BACKEND === 'firebase' && !!functions;
  const compact = variant === 'compact';

  // カレンダーの過去会議（タイトル等メタのみ）。null = 読み込み中。
  const [meetings, setMeetings] = useState<RecentMeeting[] | null>(null);
  // meetingImports の共有ステータス（リアルタイム購読）。
  const [imports, setImports] = useState<MeetingImport[]>([]);
  // インライン展開中の会議（PJ選択＋限定公開＋実行）。
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [pjChoice, setPjChoice] = useState(''); // '' = 自動判別（projectHint 体系）
  const [restricted, setRestricted] = useState(false);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [needReauth, setNeedReauth] = useState(false); // 403/401 = documents.readonly 未同意/失効
  const [successMsg, setSuccessMsg] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [showImported, setShowImported] = useState(false);

  // 会議一覧の取得（マウント時1回。トークンが変わったら＝再連携後は取り直す）。
  useEffect(() => {
    if (!googleAccessToken) {
      setMeetings(null);
      return;
    }
    let alive = true;
    void fetchRecentMeetings(googleAccessToken).then((m) => {
      if (alive) setMeetings(m);
    });
    return () => {
      alive = false;
    };
  }, [googleAccessToken]);

  // meetingImports のリアルタイム購読（誰かの取得/非表示が全員の一覧へ即反映）。
  useEffect(() => {
    let alive = true;
    let unsub: (() => void) | null = null;
    void getAdapter().then((a) => {
      if (!alive) return;
      unsub = a.subscribeMeetingImports((r) => setImports(r));
    });
    return () => {
      alive = false;
      if (unsub) unsub();
    };
  }, []);

  const importMap = useMemo(() => {
    const m = new Map<string, MeetingImport>();
    for (const r of imports) m.set(r.eventId, r);
    return m;
  }, [imports]);

  // 未処理 = レコード無し or 復元済み（pending）。fetchRecentMeetings が日付降順で返す。
  const pendingAll = useMemo(
    () =>
      (meetings ?? []).filter((m) => {
        const r = importMap.get(m.eventId);
        return !r || r.status === 'pending';
      }),
    [meetings, importMap],
  );
  // full の表示リスト（従来どおり最大10件）。
  const pendingMeetings = useMemo(() => pendingAll.slice(0, 10), [pendingAll]);
  // compact の対象：未処理かつ議事録Docありのみ（最大 COMPACT_LIMIT 件表示・超過は「他N件」）。
  const compactEligible = useMemo(
    () => pendingAll.filter((m) => m.docAttachments.length > 0),
    [pendingAll],
  );
  // 非表示/取り込み済みはレコード自身が title/date を持つため、取得窓（14日）外でも一覧できる。
  const dismissedRecords = useMemo(
    () => imports.filter((r) => r.status === 'dismissed').sort((a, b) => b.date.localeCompare(a.date)),
    [imports],
  );
  const importedRecords = useMemo(
    () => imports.filter((r) => r.status === 'imported').sort((a, b) => b.date.localeCompare(a.date)),
    [imports],
  );

  // PJ 選択肢：役員/admin = 全PJ・一般 = 所属PJ（Upload の projectHint 体系と同じ p.id）。
  const pjOptions = execView ? PROJECTS : visibleProjects(viewer);

  // 共有ステータスの upsert（byId は常に自分の安定 member id を刻む＝Rules と一致）。
  const saveImport = async (record: MeetingImportWrite): Promise<boolean> => {
    try {
      const adapter = await getAdapter();
      await adapter.setMeetingImport(record);
      return true;
    } catch (err) {
      console.warn('[MeetImportPanel] meetingImports 書き込み失敗', err instanceof Error ? err.message : err);
      setErrorMsg('状態の保存に失敗しました。時間をおいて再度お試しください。');
      return false;
    }
  };

  // 「取得しない」= 確認なしで dismissed（全員共有の非表示）。
  const dismissMeeting = (m: RecentMeeting) => {
    setErrorMsg('');
    setSuccessMsg('');
    if (openEventId === m.eventId) setOpenEventId(null);
    void saveImport({ eventId: m.eventId, title: m.title, date: m.date, status: 'dismissed', byId: viewer.id });
  };

  // 「復元」= status を pending に差し替えて未処理へ戻す（delete は admin のみのため）。
  const restoreMeeting = (r: MeetingImport) => {
    setErrorMsg('');
    void saveImport({ eventId: r.eventId, title: r.title, date: r.date, status: 'pending', byId: viewer.id });
  };

  const toggleImportPanel = (eventId: string) => {
    setOpenEventId((cur) => (cur === eventId ? null : eventId));
    setPjChoice('');
    setRestricted(false);
    setErrorMsg('');
    setNeedReauth(false);
    setSuccessMsg('');
  };

  // 取得フロー：Doc本文取得 → summarizeTranscript → imported 記録（noteId付き）。
  const runImport = async (m: RecentMeeting) => {
    // 二重取込ガード：展開済みパネルの裏で誰かが先に取得完了していたら、成功扱いで終了
    //（summarizeTranscript を再実行すると議事録・タスクが二重生成されるため）。
    if (importMap.get(m.eventId)?.status === 'imported') {
      setOpenEventId(null);
      setErrorMsg('');
      setSuccessMsg('この会議は既に取り込み済みです。「取り込み済み」の一覧から議事録を開けます。');
      return;
    }
    const docFile = m.docAttachments[0];
    if (!googleAccessToken || !functions || !aiEnabled || !docFile) return;
    setBusyEventId(m.eventId);
    setErrorMsg('');
    setNeedReauth(false);
    setSuccessMsg('');
    try {
      const text = await fetchGoogleDocText(googleAccessToken, docFile.fileId);
      if (text.length < 20) {
        setErrorMsg('議事録ドキュメントの本文が短すぎるため取得できませんでした。');
        return;
      }
      const callable = httpsCallable<SummarizePayload, SummarizeResult>(functions, 'summarizeTranscript');
      const payload: SummarizePayload = {
        transcriptText: text,
        restricted,
        titleHint: m.title,
        dateHint: m.date,
        // 出席者メール（サーバー側で members の email と突合して participantIds を解決）。
        attendeeEmails: m.attendeeEmails.slice(0, 50),
      };
      if (pjChoice) payload.projectHint = pjChoice;
      const res = await callable(payload);
      // 共有ステータスに imported を記録（誰か1人が取得すれば全員分完了）。
      // ★限定公開の会議は meetingImports（登録メンバー全員 read 可）にタイトルを残さない
      //   （会議名だけでも漏れるため）。date は復元・並び替えに必要なのでそのまま。
      await saveImport({
        eventId: m.eventId,
        title: restricted ? '（限定公開の会議）' : m.title,
        date: m.date,
        status: 'imported',
        byId: viewer.id,
        noteId: res.data.noteId,
      });
      setOpenEventId(null);
      setSuccessMsg(
        '「' + res.data.title + '」の議事録とタスク' + res.data.taskCount + '件を作成しました。下の議事録一覧に反映されます。',
      );
    } catch (err) {
      if (err instanceof GoogleApiError && (err.status === 401 || err.status === 403)) {
        // documents.readonly 未同意（スコープ追加後の既存ユーザー）or トークン失効。
        setNeedReauth(true);
      } else if (err instanceof GoogleApiError && err.status === 404) {
        setErrorMsg('議事録ドキュメントが見つかりませんでした（削除済み・権限がない可能性があります）。');
      } else {
        setErrorMsg(err instanceof Error ? err.message : '議事録の取得に失敗しました。');
      }
    } finally {
      setBusyEventId(null);
    }
  };

  // Google 未連携（DEV/mock・トークン失効）：full は1行案内・compact はパネルごと非表示。
  if (!googleAccessToken) {
    if (compact) return null;
    return (
      <div className="mt-5 flex items-center gap-2.5 rounded-card border border-hair bg-white px-4 py-3 text-xs text-slate-sub">
        <Icon name="video" size={15} />
        <span>Google連携で会議一覧が表示されます</span>
      </div>
    );
  }

  // compact：読み込み完了かつ対象0件（＝出すものがない）なら非表示。
  // 直前の取得成功/エラー/再連携案内が出ている間は、フィードバックを見せるため残す。
  if (compact && meetings !== null && compactEligible.length === 0 && !successMsg && !errorMsg && !needReauth) {
    return null;
  }
  // compact：読み込み中はまだ何も出さない（ホームを汚さない）。
  if (compact && meetings === null) return null;

  // 表示する会議リスト：full = 従来の最大10件（Docなし含む）／compact = Docありのみ最大3件。
  const listMeetings = compact ? compactEligible.slice(0, COMPACT_LIMIT) : pendingMeetings;
  const compactMore = compact ? Math.max(0, compactEligible.length - COMPACT_LIMIT) : 0;

  return (
    <section
      className={
        compact
          ? 'rounded-card border border-hair bg-white p-4 sm:p-5'
          : 'mt-5 rounded-card border border-hair bg-white px-4 py-4'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="video" size={16} className="text-sky-brand" />
        <h2 className="text-sm font-semibold text-charcoal">Google Meet から取り込む</h2>
        <span className="text-2xs text-slate-sub">直近14日の会議</span>
      </div>
      {!compact && (
        <p className="mt-1 text-2xs text-slate-sub">
          「議事録を取得する」を押した会議だけ、添付の議事録ドキュメントを読み取ってAIが議事録・タスク化します（数十秒）。誰か1人が取得すれば全員に共有されます。
        </p>
      )}

      {/* 403（スコープ未同意/失効）→ 再連携導線 */}
      {needReauth && (
        <div className="mt-3 flex flex-wrap items-center gap-2.5 rounded-soft border border-amber-brand/25 bg-amber-50/60 px-3.5 py-3 text-xs text-amber-900">
          <Icon name="triangle-alert" size={14} className="flex-shrink-0" />
          <span className="min-w-0 flex-1">
            Googleへの再連携が必要です（議事録ドキュメントの読み取り権限を追加します）。
          </span>
          <button
            type="button"
            onClick={() => void signInWithGoogle()}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-soft bg-charcoal hover:bg-charcoal-700 text-white text-xs font-medium transition active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal/40"
          >
            <Icon name="link" size={13} /> Googleと再連携する
          </button>
        </div>
      )}

      {errorMsg && !needReauth && (
        <div className="mt-3 flex items-start gap-2 rounded-soft border border-danger/20 bg-danger/5 px-3.5 py-2.5 text-xs text-danger">
          <Icon name="circle-alert" size={14} className="mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {successMsg && (
        <div className="mt-3 flex items-start gap-2 rounded-soft border border-success/25 bg-success/5 px-3.5 py-2.5 text-xs text-success">
          <Icon name="circle-check" size={14} className="mt-0.5 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* 未処理の会議リスト（日付降順。full=最大10件／compact=Docありのみ最大3件） */}
      {meetings === null ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-sub">
          <Icon name="loader" size={14} className="animate-spin" /> 会議一覧を読み込み中…
        </div>
      ) : listMeetings.length === 0 ? (
        <div className="mt-3 rounded-soft border border-dashed border-line px-4 py-4 text-center text-xs text-slate-sub">
          未処理の会議はありません。
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {listMeetings.map((m) => {
            const hasDoc = m.docAttachments.length > 0;
            const busy = busyEventId === m.eventId;
            return (
              <li key={m.eventId} className="rounded-soft border border-hair px-3.5 py-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-charcoal truncate">{m.title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-2xs text-slate-sub">
                      <span className="font-num">
                        {m.date}
                        {m.time ? ' ' + m.time : ''}
                      </span>
                      {m.attendeeCount > 0 && <span>参加者 {m.attendeeCount}人</span>}
                      {hasDoc ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium"
                          style={{ background: '#E0F2FE', color: '#0369A1' }}
                        >
                          <Icon name="file-text" size={11} /> 議事録Docあり
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-base px-2 py-0.5">
                          <Icon name="file-text" size={11} /> 議事録が見つかりません
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    {hasDoc && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => toggleImportPanel(m.eventId)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-soft bg-charcoal hover:bg-charcoal-700 text-white text-xs font-medium transition active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal/40"
                      >
                        <Icon name="sparkles" size={13} /> 議事録を取得する
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => dismissMeeting(m)}
                      className="text-2xs text-slate-sub hover:text-charcoal underline underline-offset-2 disabled:opacity-40"
                    >
                      取得しない
                    </button>
                  </div>
                </div>

                {/* インライン展開：PJ選択＋限定公開＋実行 */}
                {openEventId === m.eventId && hasDoc && (
                  <div className="mt-3 rounded-soft border border-hair bg-base/40 px-3.5 py-3">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <label className="text-xs font-medium text-slate-sub" htmlFor={'meet-pj-' + variant + '-' + m.eventId}>
                        取込先PJ
                      </label>
                      <select
                        id={'meet-pj-' + variant + '-' + m.eventId}
                        value={pjChoice}
                        onChange={(e) => setPjChoice(e.target.value)}
                        disabled={busy}
                        className="h-9 rounded-soft border border-hair bg-white px-3 text-sm outline-none focus:border-charcoal disabled:bg-base disabled:text-slate-sub"
                      >
                        <option value="">自動判別（AIにおまかせ）</option>
                        {pjOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <label className="mt-2.5 flex items-start gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={restricted}
                        onChange={(e) => setRestricted(e.target.checked)}
                        disabled={busy}
                        className="mt-0.5 h-4 w-4 accent-charcoal"
                      />
                      <span className="flex items-center gap-1.5 text-xs font-medium text-charcoal">
                        <Icon name="lock" size={13} className="text-slate-sub" /> 参加者と役員のみに公開（限定公開）
                      </span>
                    </label>
                    <div className="mt-3 flex flex-wrap items-center gap-2.5">
                      <button
                        type="button"
                        disabled={busy || !aiEnabled}
                        onClick={() => void runImport(m)}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-soft bg-charcoal hover:bg-charcoal-700 text-white text-sm font-medium transition active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal/40"
                      >
                        {busy ? (
                          <>
                            <Icon name="loader" size={14} className="animate-spin" /> 取得中…（数十秒かかります）
                          </>
                        ) : (
                          <>
                            <Icon name="sparkles" size={14} /> 実行
                          </>
                        )}
                      </button>
                      {!aiEnabled && (
                        <span className="text-2xs text-slate-sub">
                          AI議事録化はライブ（firebase）接続時のみ利用できます。
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* compact：残件数＋一覧への導線（非表示/取り込み済みの折りたたみは /notes 側で） */}
      {compact && (
        <div className="mt-3 text-right">
          <Link to="/notes" className="text-xs font-medium text-sky-deep hover:underline">
            {compactMore > 0 ? '他 ' + compactMore + ' 件・すべて見る' : 'すべて見る'}
          </Link>
        </div>
      )}

      {/* 折りたたみ：非表示にした会議（復元可能）※ full のみ */}
      {!compact && dismissedRecords.length > 0 && (
        <div className="mt-4 border-t border-hair pt-3">
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-slate-sub hover:text-charcoal focus-visible:outline-none"
          >
            <Icon name={showHidden ? 'chevron-up' : 'chevron-down'} size={14} />
            <Icon name="eye-off" size={13} /> 非表示にした会議 {dismissedRecords.length}件
          </button>
          {showHidden && (
            <ul className="mt-2 space-y-1.5">
              {dismissedRecords.map((r) => (
                <li key={r.eventId} className="flex items-center gap-3 rounded-soft bg-base/60 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-sub">{r.title}</span>
                  <span className="text-2xs font-num text-slate-400">{r.date}</span>
                  <button
                    type="button"
                    onClick={() => restoreMeeting(r)}
                    className="inline-flex items-center gap-1 text-2xs font-medium text-charcoal hover:underline underline-offset-2"
                  >
                    <Icon name="rotate-ccw" size={12} /> 復元
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 折りたたみ：取り込み済みの会議（議事録リンク付き）※ full のみ */}
      {!compact && importedRecords.length > 0 && (
        <div className={(dismissedRecords.length > 0 ? 'mt-3' : 'mt-4 border-t border-hair pt-3')}>
          <button
            type="button"
            onClick={() => setShowImported((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-slate-sub hover:text-charcoal focus-visible:outline-none"
          >
            <Icon name={showImported ? 'chevron-up' : 'chevron-down'} size={14} />
            <Icon name="circle-check" size={13} className="text-success" /> 取り込み済み {importedRecords.length}件
          </button>
          {showImported && (
            <ul className="mt-2 space-y-1.5">
              {importedRecords.map((r) => (
                <li key={r.eventId} className="flex items-center gap-3 rounded-soft bg-base/60 px-3 py-2">
                  <Icon name="circle-check" size={13} className="text-success flex-shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-sub">{r.title}</span>
                  <span className="text-2xs font-num text-slate-400">{r.date}</span>
                  {r.noteId && (
                    <Link
                      to={'/notes/' + r.noteId}
                      className="inline-flex items-center gap-1 text-2xs font-medium text-charcoal hover:underline underline-offset-2"
                    >
                      議事録を開く <Icon name="arrow-right" size={11} />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
